import {
  planzoneSettings,
  projectElementDetailsConfiguration,
} from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveProjectElementDetailsConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveProjectElementDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: projectElementDetailsConfiguration,
        },
      ];
}

function handleRetrieveProjectElementDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveProjectElementDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveProjectElementDetailsConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveProjectElementDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: projectElementDetailsConfiguration,
        },
      ];
}

function handleEditProjectElementDetailsConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editProjectElementDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditProjectElementDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectElementDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditProjectElementDetailsConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectElementDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveProjectElementDetailsConfigurationRequested]:
    handleRetrieveProjectElementDetailsConfigurationRequest,
  [events.retrieveProjectElementDetailsConfigurationSucceeded]:
    handleRetrieveProjectElementDetailsConfigurationSuccess,
  [events.retrieveProjectElementDetailsConfigurationFailed]:
    handleRetrieveProjectElementDetailsConfigurationFailure,
  [events.editProjectElementDetailsConfigurationRequested]:
    handleEditProjectElementDetailsConfigurationRequest,
  [events.editProjectElementDetailsConfigurationSucceeded]:
    handleEditProjectElementDetailsConfigurationSuccess,
  [events.editProjectElementDetailsConfigurationFailed]:
    handleEditProjectElementDetailsConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
